import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isBefore } from 'date-fns';
import {
  ClassicMigrationContentVariant,
  ClassicMigrationOnboardingModal,
  LevelBasedPointsModal,
  MyProgressIntroModal,
  ProficiencyLevelModal,
  WelcomePopup,
} from 'src/components';
import { useUserContext } from 'src/context/user';
import {
  addToLocalStorageArrayEntry,
  hasInLocalStorageArrayEntry,
  removeFromLocalStorageArrayEntry,
} from 'src/utils/webStorageArrayUtils';
import {
  CLASSIC_MIGRATION_ONBOARDING_STORAGE_KEY,
  EXPLAIN_LEVEL_BASED_POINTS_STORAGE_KEY,
  MY_WORDS_INTRO_STORAGE_KEY,
  SHOULD_DISPLAY_MY_ACTIVITIES_INTRO_STORAGE_KEY,
  PRE_RELEASE_USERS_SEEN_MY_ACTIVITIES_STORAGE_KEY,
  MIGRATED_TO_MY_PROGRESS_STORAGE_KEY,
  WELCOME_TO_MEMRISE_STORAGE_KEY,
} from 'src/constants/storageKeys.const';
import { getLocalStorageEntry, removeLocalStorageEntry } from 'src/utils/webStorageUtils';
import { Activities } from 'src/components/MiniPlayer';
import { MyWordsIntroModal } from 'src/apps/MyWords/MyWordsIntroModal';
import { MyActivitiesIntroModal } from 'src/apps/Activities/MyActivitiesIntroModal';

type ModalKey =
  | 'welcome'
  | 'intro-points'
  | 'intro-my-words'
  | 'intro-my-activities'
  | 'migration-my-progress'
  | 'migration-proficiency'
  | 'new-feature-my-words'
  | 'classic-migration-levels'
  | 'classic-migration-scenarios'
  | 'classic-migration-dictionary';

export type HomeModalsProps = {
  languagePairId: number | undefined;
  supportedActivities: Activities[] | null;
};

type ModalConfig = {
  key: ModalKey;
  onClose: () => void;
};

const MY_PROGRESS_RELEASE_DATE = '2024-06-24T00:00:00Z';
const MY_ACTIVITIES_RELEASE_DATE = '2025-04-01T10:00:00Z';

export function useHomeModals({ languagePairId, supportedActivities }: HomeModalsProps) {
  const { user, softRefetchUser } = useUserContext();
  const [modalsToDisplay, setModalsToDisplay] = useState<ModalConfig[]>([]);

  useEffect(() => {
    // Ensure modals are set only once to avoid interrupting the user multiple times
    if (modalsToDisplay.length !== 0) return;

    // Wait until all necessary data is available to prevent recalculating modals later
    if (!languagePairId || !user?.id || !supportedActivities) return;

    const showMyProgressMigration =
      isBefore(user.dateJoined, MY_PROGRESS_RELEASE_DATE) &&
      !hasInLocalStorageArrayEntry(MIGRATED_TO_MY_PROGRESS_STORAGE_KEY, String(user.id));

    const showClassicMigration =
      getLocalStorageEntry(CLASSIC_MIGRATION_ONBOARDING_STORAGE_KEY) === 'true';

    setModalsToDisplay(
      [
        {
          key: 'welcome' as ModalKey,
          shouldDisplay:
            supportedActivities.length === 3 &&
            hasInLocalStorageArrayEntry(WELCOME_TO_MEMRISE_STORAGE_KEY, String(user.id)),
          onClose: () =>
            removeFromLocalStorageArrayEntry(WELCOME_TO_MEMRISE_STORAGE_KEY, String(user?.id)),
        },
        {
          key: 'classic-migration-levels' as ModalKey,
          shouldDisplay: showClassicMigration,
          onClose: () => {},
        },
        {
          key: 'classic-migration-scenarios' as ModalKey,
          shouldDisplay: showClassicMigration,
          onClose: () => {},
        },
        {
          key: 'classic-migration-dictionary' as ModalKey,
          shouldDisplay: showClassicMigration,
          onClose: () => {
            removeLocalStorageEntry(CLASSIC_MIGRATION_ONBOARDING_STORAGE_KEY);
            addToLocalStorageArrayEntry(MIGRATED_TO_MY_PROGRESS_STORAGE_KEY, String(user.id));
          },
        },
        {
          key: 'intro-points' as ModalKey,
          shouldDisplay: hasInLocalStorageArrayEntry(
            EXPLAIN_LEVEL_BASED_POINTS_STORAGE_KEY,
            `${user.id}-${languagePairId}`,
          ),
          onClose: () =>
            removeFromLocalStorageArrayEntry(
              EXPLAIN_LEVEL_BASED_POINTS_STORAGE_KEY,
              `${user.id}-${languagePairId}`,
            ),
        },
        {
          key: 'migration-my-progress' as ModalKey,
          shouldDisplay: showMyProgressMigration,
          onClose: () => {},
        },
        {
          key: 'migration-proficiency' as ModalKey,
          shouldDisplay: showMyProgressMigration,
          onClose: () => {},
        },
        {
          key: 'new-feature-my-words' as ModalKey,
          shouldDisplay: showMyProgressMigration,
          onClose: () =>
            addToLocalStorageArrayEntry(MIGRATED_TO_MY_PROGRESS_STORAGE_KEY, String(user?.id)),
        },
        {
          key: 'intro-my-words' as ModalKey,
          shouldDisplay: getLocalStorageEntry(MY_WORDS_INTRO_STORAGE_KEY) === 'true',
          onClose: () => {
            removeLocalStorageEntry(MY_WORDS_INTRO_STORAGE_KEY);
            softRefetchUser();
          },
        },
        {
          key: 'intro-my-activities' as ModalKey,
          shouldDisplay:
            getLocalStorageEntry(SHOULD_DISPLAY_MY_ACTIVITIES_INTRO_STORAGE_KEY) === 'true' ||
            (isBefore(user.dateJoined, MY_ACTIVITIES_RELEASE_DATE) &&
              !hasInLocalStorageArrayEntry(
                PRE_RELEASE_USERS_SEEN_MY_ACTIVITIES_STORAGE_KEY,
                String(user.id),
              )),
          onClose: () => {
            removeLocalStorageEntry(SHOULD_DISPLAY_MY_ACTIVITIES_INTRO_STORAGE_KEY);
            addToLocalStorageArrayEntry(
              PRE_RELEASE_USERS_SEEN_MY_ACTIVITIES_STORAGE_KEY,
              String(user.id),
            );
          },
        },
      ].filter(({ shouldDisplay }) => shouldDisplay),
    );
  }, [
    user?.id,
    user?.dateJoined,
    softRefetchUser,
    languagePairId,
    supportedActivities,
    modalsToDisplay?.length,
  ]);

  const [openModal, setOpenModal] = useState<ModalConfig | null>(null);
  const [displayedModals, setDisplayedModals] = useState<ModalKey[]>([]);

  const isModalOpen = useCallback((modalKey: ModalKey) => openModal?.key === modalKey, [openModal]);

  const handleClose = useCallback(() => {
    /* istanbul ignore if: should not happen */
    if (!openModal) return;

    setDisplayedModals(prevState => [...prevState, openModal?.key]);
    openModal.onClose();

    const nextModal = modalsToDisplay.find(
      m => m.key !== openModal.key && !displayedModals.includes(m.key),
    );
    setOpenModal(nextModal || null);
  }, [openModal, displayedModals, modalsToDisplay]);

  const hasAllProps = Boolean(user && languagePairId && supportedActivities);

  const progressIndicator = useMemo(
    () =>
      modalsToDisplay.length > 2 ? `${displayedModals.length + 1}/${modalsToDisplay.length}` : '',
    [modalsToDisplay.length, displayedModals.length],
  );

  useEffect(() => {
    if (!openModal && hasAllProps) {
      const nextModal = modalsToDisplay.find(m => !displayedModals.includes(m.key));

      if (nextModal) {
        setOpenModal(nextModal);
      }
    }
  }, [openModal, modalsToDisplay, displayedModals, hasAllProps]);

  return useMemo(
    () => [
      <WelcomePopup
        key="welcome"
        isOpen={isModalOpen('welcome')}
        onClose={handleClose}
        progressIndicator={progressIndicator}
      />,
      <LevelBasedPointsModal
        key="points"
        isOpen={isModalOpen('intro-points')}
        onClose={handleClose}
        progressIndicator={progressIndicator}
      />,
      <MyProgressIntroModal
        key="migration-my-progress"
        isOpen={isModalOpen('migration-my-progress')}
        onClose={handleClose}
        progressIndicator={progressIndicator}
      />,
      <ProficiencyLevelModal
        key="migration-proficiency"
        isOpen={isModalOpen('migration-proficiency')}
        onClose={handleClose}
        progressIndicator={progressIndicator}
      />,
      <MyWordsIntroModal
        key="dictionary"
        isOpen={isModalOpen('intro-my-words') || isModalOpen('new-feature-my-words')}
        contentVariant={openModal?.key === 'new-feature-my-words' ? 'new-feature' : 'intro'}
        onClose={handleClose}
        progressIndicator={progressIndicator}
      />,
      <MyActivitiesIntroModal
        key="my-activities"
        isOpen={isModalOpen('intro-my-activities')}
        onClose={handleClose}
        progressIndicator={progressIndicator}
      />,
      <ClassicMigrationOnboardingModal
        key="classic-migration"
        isOpen={
          isModalOpen('classic-migration-levels') ||
          isModalOpen('classic-migration-scenarios') ||
          isModalOpen('classic-migration-dictionary')
        }
        contentVariant={openModal?.key as ClassicMigrationContentVariant}
        onClose={handleClose}
        progressIndicator={progressIndicator}
      />,
    ],
    [isModalOpen, openModal?.key, handleClose, progressIndicator],
  );
}
